import apollo from '@/apollo.js'
import gql from 'graphql-tag'

const bucketDebugLog = (message, context) => {
  if (typeof window !== 'undefined' && window.__CB_DEBUG_BUCKET__) {
    console.log('[findBucket]', message, context != null ? context : '')
  }
}

export const findBucket = async (bucketId) => {
  try {
    // Defensive check: ensure bucketId is provided
    if (!bucketId) {
      console.warn('[findBucket] Defensive: bucketId is missing')
      bucketDebugLog('Defensive path: bucketId missing', { bucketId })
      return null
    }

    bucketDebugLog('Calling bucket GraphQL query', { bucketId })
    const response = await apollo.query({
      query: gql(`
        query BucketById($id: Int!) {
          bucket(id: $id) {
            id
            name
            caseTypeName
            description
            kpi
            displayGroupByPanel
            showUserPresence
            advancedQuery
            default
            ownedById
            ownedByType
            owner {
              __typename
              ... on Participating {
                displayName
              }
            }
            contents(page: 0, pageSize: 1) {
              statistics { total }
            } 
            criteria {
              id
              fieldset
              fieldName
              condition
              value
            }
            displayFields {
              id
              fieldset
              fieldName
              position
            }
            sortFields {
              id
              fieldset
              fieldName
              position
              direction
            }
          }
        }
      `),
      variables: { id: bucketId }
    })
    
    // Defensive check: ensure response and data exist
    if (!response || !response.data) {
      console.warn('[findBucket] Defensive: Invalid response from API', { bucketId })
      bucketDebugLog('Defensive path: invalid response', { bucketId, hasResponse: !!response })
      return null
    }

    const bucket = response.data.bucket || null
    if (!bucket) {
      console.warn('[findBucket] Defensive: API returned no bucket', { bucketId })
      bucketDebugLog('Defensive path: no bucket in response', { bucketId })
    }
    return bucket
  } catch (error) {
    console.error('[findBucket] Error fetching bucket', error, { bucketId })
    bucketDebugLog('Defensive path: exception', { bucketId, message: error && error.message })
    return null
  }
}

export const addBucketMember = async (bucketId, participantId, participantType) => {
  const response = await apollo.mutate(
    {
      mutation: gql(`
            mutation addBucketParticipant($bucketId: Int!, $participantId: Int!, $participantType: UserOrTeam!) {
              addBucketParticipant(bucketId: $bucketId, participantId: $participantId, participantType: $participantType) {
                error
                lowLevelErrorMessage
                message
              }
            }
        `),
      variables: { bucketId: bucketId, participantId: participantId, participantType: participantType }
    })
  return response.data
}

export const removeBucketMember = async (bucketId, participantId, participantType) => {
  const response = await apollo.mutate(
    {
      mutation: gql(`
            mutation removeBucketParticipant($bucketId: Int!, $participantId: Int!, $participantType: UserOrTeam!) {
              removeBucketParticipant(bucketId: $bucketId, participantId: $participantId, participantType: $participantType) {
                error
                lowLevelErrorMessage
                message
              }
            }
        `),
      variables: { bucketId: bucketId, participantId: participantId, participantType: participantType }
    })
  return response.data
}

export const findOrderedBucket = async (bucketId, page, pageSize, sortField, sortDirection, searchTerm, searchableBy) => {
  if (page > 0) { page = page - 1 } // Pagination component starts at page 1, query page starts at 0.
  const response = await apollo.query({
    query: gql(`
      query bucket($id: Int!, $page: Int!, $pageSize: Int!, $sortField: String, $sortDirection: SortDirection, $searchTerm: String, $searchField: String) {
        bucket(id: $id) { 
          id
          name
          caseTypeName
          description
          advancedQuery
          serviceLevel {
            id
            bucketId
            enabled
            name
            inJeopardyThreshold { days hours minutes }
            inBreachThreshold { days hours minutes }
          }
          default
          kpi
          displayGroupByPanel
          showUserPresence
          owner {
            __typename
            ... on Participating {
              displayName
            }
          }    
          participants {
            __typename
            ... on Participating {
              id
              displayName
            }
          }                
          criteria { id fieldset fieldName condition value }
          displayFields { id fieldset fieldName position }
          sortFields { id fieldset fieldName position direction }
          contents(page: $page, pageSize: $pageSize, sortField: $sortField, sortDirection: $sortDirection, searchTerm: $searchTerm, searchField: $searchField) {
            cases {
              id title currentState createdAt updatedAt lastExportedAt lastExportedViaBucketId noUnreadConversations unresolvedTaskFailures properties currentlyViewing { userId, userDisplayName } sentimentScore sentimentMagnitude serviceLevels { jeopardizedAt breachedAt runningTime serviceLevelCriteria { id } }
            }
            statistics { totalCreated24hrs totalUpdated24hrs total kpis { value key } }
            appliedSortFields { id fieldset fieldName position direction }
          }    
        }
      }
    `),
    variables: { id: bucketId, page: page, pageSize: pageSize, sortField: sortField, sortDirection: sortDirection, searchTerm: searchTerm, searchField: searchableBy, mapField: "located_at" }
  })
  return response.data
}

export const findBucketMapPoints = async (bucketId, searchTerm, searchableBy, mapField) => {
  const response = await apollo.query({
    query: gql(`
      query bucket($id: Int!, $searchTerm: String, $searchField: String, $field: String!) {
        bucket(id: $id) { 
          id   
          mapPoints(field:$field, searchTerm: $searchTerm, searchField: $searchField) { id title latitude longitude } 
        }
      }
    `),
    variables: { id: bucketId, searchTerm: searchTerm, searchField: searchableBy, field: mapField }
  })
  return response.data
}

export const createBucket = async (bucket) => {
  const bucketResponse = await apollo.mutate(
    {
      mutation: gql(`
        mutation createBucket($caseTypeId: Int!, $name: String!, $description: String, $kpi: String!, $advancedQuery: String, $displayGroupByPanel: Boolean!, $showUserPresence: Boolean!) {
          createBucket(caseTypeId: $caseTypeId, name: $name, description: $description, kpi: $kpi, advancedQuery: $advancedQuery, displayGroupByPanel: $displayGroupByPanel, showUserPresence: $showUserPresence) {
            bucket { 
              id 
              name 
              description 
              kpi 
              advancedQuery 
              caseTypeName
              displayGroupByPanel 
              showUserPresence
              contents(page:0, pageSize: 10) {
                statistics {
                  kpis { value key }
                  total
                  totalCreated24hrs
                  totalUpdated24hrs
                }
              }
            } error message
          }
        }
      `),
      variables: {
        caseTypeId: bucket.caseTypeId,
        name: bucket.name,
        description: bucket.description,
        kpi: bucket.kpi,
        advancedQuery: bucket.advancedQuery,
        displayGroupByPanel: bucket.displayGroupByPanel,
        showUserPresence: !!bucket.showUserPresence
      }
    }
  )

  let returnedBucket
  if (bucketResponse && bucketResponse.data && bucketResponse.data.createBucket && bucketResponse.data.createBucket.bucket && bucketResponse.data.createBucket.bucket.id) {
    const bucketId = bucketResponse.data.createBucket.bucket.id
    returnedBucket = bucketResponse.data.createBucket.bucket

    // Display fields
    returnedBucket.displayFields = []
    for (let x = 0; x < bucket.displayFields.length; x++) {
      const displayField = bucket.displayFields[x]
      const displayFieldsResponse = await apollo.mutate(
        {
          mutation: gql(`
            mutation createBucketDisplayField($bucketId: Int!, $fieldSet: String!, $fieldName: String!, $position: Int!) {
              createBucketDisplayField(bucketId: $bucketId, fieldSet: $fieldSet, fieldName: $fieldName, position: $position) {
                bucketDisplayField { id fieldset fieldName position } error message
              }
            }
          `),
          variables: {
            bucketId: bucketId,
            fieldSet: displayField.fieldset,
            fieldName: displayField.fieldName,
            position: displayField.position
          }
        }
      )
      if (displayFieldsResponse && displayFieldsResponse.data && displayFieldsResponse.data.createBucketDisplayField.bucketDisplayField) {
        returnedBucket.displayFields.push(displayFieldsResponse.data.createBucketDisplayField.bucketDisplayField)
      }

      // Add it as a sort field
      if (displayField.sortPosition > 0) {
        await apollo.mutate(
          {
            mutation: gql(`
              mutation createBucketSortField($bucketId: Int!, $fieldSet: String!, $fieldName: String!, $position: Int!, $direction: String!) {
                createBucketSortField(bucketId: $bucketId, fieldSet: $fieldSet, fieldName: $fieldName, position: $position, direction: $direction) {
                  bucketSortField { id fieldset fieldName position direction } error message
                }
              }
            `),
            variables: {
              bucketId: bucketId,
              fieldSet: displayField.fieldset,
              fieldName: displayField.fieldName,
              position: displayField.sortPosition,
              direction: displayField.sortDirection
            }
          }
        )
      }
    }

    // Filter fields
    returnedBucket.criteria = []
    for (let x = 0; x < bucket.criteria.length; x++) {
      const criteriaField = bucket.criteria[x]
      const criteriaResponse = await apollo.mutate(
        {
          mutation: gql(`
            mutation createBucketCriteria($bucketId: Int!, $fieldSet: String!, $fieldName: String!, $condition: String!, $value: String) {
              createBucketCriteria(bucketId: $bucketId, fieldSet: $fieldSet, fieldName: $fieldName, condition: $condition, value: $value) {
                bucketCriteria { id fieldset fieldName condition value } error message
              }
            }
          `),
          variables: {
            bucketId: bucketId,
            fieldSet: criteriaField.fieldset,
            fieldName: criteriaField.fieldName,
            condition: criteriaField.condition,
            value: criteriaField.value
          }
        }
      )
      if (criteriaResponse && criteriaResponse.data && criteriaResponse.data.createBucketCriteria.bucketCriteria) {
        returnedBucket.displayFields.push(criteriaResponse.data.createBucketCriteria.bucketCriteria)
      }
    }
    return bucketResponse
  } else {
    return undefined
  }
}

export const updateBucketOwner = async (bucket, ownerId, ownerType) => {
  const response = await apollo.mutate(
    {
      mutation: gql(`
        mutation updateBucketOwner($id: Int!, $ownerId: Int!, $ownerType: UserOrTeam!) {
          updateBucketOwner(id: $id, ownerId: $ownerId, ownerType: $ownerType) {
            error
            message
            lowLevelErrorMessage
          }
        }
      `),
      variables: {
        id: bucket.id,
        ownerId: ownerId,
        ownerType: ownerType
      }
    }
  )
  return response.data.updateBucketOwner
}

export const updateBucket = async (bucket) => {
  const response = await apollo.mutate(
    {
      mutation: gql(`
        mutation updateBucket($id: Int!, $name: String!, $description: String, $kpi: String!, $advancedQuery: String, $displayGroupByPanel: Boolean!, $showUserPresence: Boolean!) {
          updateBucket(id: $id, name: $name, description: $description,  kpi: $kpi, advancedQuery: $advancedQuery, displayGroupByPanel: $displayGroupByPanel, showUserPresence: $showUserPresence) {
            bucket { id name description kpi displayGroupByPanel showUserPresence } error message
          }
        }
      `),
      variables: {
        id: bucket.id,
        name: bucket.name,
        description: bucket.description,
        kpi: bucket.kpi,
        advancedQuery: bucket.advancedQuery,
        displayGroupByPanel: bucket.displayGroupByPanel,
        showUserPresence: !!bucket.showUserPresence
      }
    }
  )
  return (response && response.data && response.data.updateBucket && !response.data.updateBucket.bucket)
}

export const exportBucket = async (bucketId) => {
  const response = await apollo.mutate(
    {
      mutation: gql(`
        mutation exportBucket($id: Int!) {
          exportBucket(id: $id) {
          job { id }
            error
            message
            lowLevelErrorMessage
          }
        }
      `),
      variables: { id: bucketId }
    }
  )
  return response.data.exportBucket
}

export const deleteBucket = async (bucketId) => {
  const response = await apollo.mutate(
    {
      mutation: gql(`
        mutation deleteBucket($id: Int!) {
          deleteBucket(id: $id) {
            error message
          }
        }
      `),
      variables: { id: bucketId }
    }
  )
  return response.data.deleteBucket
}

export const getBucketsByCaseType = async (caseTypeId) => {
  const response = await apollo.mutate(
    {
      mutation: gql(`
        query buckets($caseTypeId: Int!) {
          buckets(caseTypeId: $caseTypeId) {
            id
            name
            caseTypeName
            contents(page: 0, pageSize: 1) {
              statistics {
                total
                totalCreated24hrs
                totalUpdated24hrs
                kpis { key value }
              }
            }
          }
        }
      `),
      variables: { caseTypeId }
    }
  )
  return response.data
}
